import { COMMISSION_ENDPOINTS } from '../constants/api';

/**
 * Commission Tracker Service
 * Handles all commission tracking operations for ISR commission automation
 * This implements the functionality from your Google Apps Script commission system
 */
class CommissionTrackerService {
  /**
   * Generate commission records from "Ready for Commission" match results
   * Equivalent to updateCommissionTracker() function from Apps Script
   */
  async generateCommissionTracker(sessionId) {
    try {
      const response = await fetch(COMMISSION_ENDPOINTS.COMMISSION_TRACKER_GENERATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sessionId })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate commission tracker');
      }

      return data;
    } catch (error) {
      console.error('Error generating commission tracker:', error);
      throw error;
    }
  }

  /**
   * Get commission tracker records with filtering and pagination
   * Supports all the filtering options needed for commission management
   */
  async getCommissionTracker(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      // Add filters to query string
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const url = `${COMMISSION_ENDPOINTS.COMMISSION_TRACKER}?${queryParams.toString()}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch commission tracker');
      }

      return data;
    } catch (error) {
      console.error('Error fetching commission tracker:', error);
      throw error;
    }
  }

  /**
   * Update commission record status (Pending, Approved, Paid, etc.)
   */
  async updateCommissionStatus(recordId, status, notes = null) {
    try {
      const response = await fetch(COMMISSION_ENDPOINTS.COMMISSION_TRACKER_UPDATE_STATUS(recordId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, notes })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update commission status');
      }

      return data;
    } catch (error) {
      console.error('Error updating commission status:', error);
      throw error;
    }
  }

  /**
   * Get monthly commission summary
   * Equivalent to generateSummary() function from Apps Script
   */
  async getMonthlySummary(month) {
    try {
      const response = await fetch(COMMISSION_ENDPOINTS.COMMISSION_SUMMARY(month), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch monthly summary');
      }

      return data;
    } catch (error) {
      console.error('Error fetching monthly summary:', error);
      throw error;
    }
  }

  /**
   * Get individual rep performance report
   */
  async getRepPerformance(repName, filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      // Add filters for date range
      if (filters.startMonth) queryParams.append('startMonth', filters.startMonth);
      if (filters.endMonth) queryParams.append('endMonth', filters.endMonth);

      const url = `${COMMISSION_ENDPOINTS.REP_PERFORMANCE(repName)}?${queryParams.toString()}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch rep performance');
      }

      return data;
    } catch (error) {
      console.error('Error fetching rep performance:', error);
      throw error;
    }
  }

  /**
   * Get commission status options
   */
  getCommissionStatuses() {
    return [
      { value: 'Pending', label: 'Pending', color: '#f59e0b' },
      { value: 'Approved', label: 'Approved', color: '#10b981' },
      { value: 'Paid', label: 'Paid', color: '#059669' },
      { value: 'Disputed', label: 'Disputed', color: '#ef4444' },
      { value: 'Cancelled', label: 'Cancelled', color: '#6b7280' }
    ];
  }

  /**
   * Get ISR list from backend (matches your Apps Script salesRepLookup)
   */
  getISRList() {
    return [
      { id: '121288978', name: 'Tyson Queen', isManager: true },
      { id: '217821963', name: 'Irvin Trahan III', isManager: false },
      { id: '1123062687', name: 'Charity Grandchampt', isManager: false },
      { id: '849604905', name: 'Joel Thomas', isManager: false },
      { id: '76877995', name: 'Joshua Thomas', isManager: false },
      { id: '76877994', name: 'Crystal Robbins', isManager: false },
      { id: '1024354151', name: 'Casey Jones', isManager: false },
      { id: '81162966', name: 'Rayvion Williams', isManager: false }
    ];
  }

  /**
   * Format currency values
   */
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  /**
   * Format month for display and API calls
   */
  formatMonth(date) {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    if (typeof date === 'string') {
      return date; // Already formatted
    }
    
    const dateObj = new Date(date);
    return `${monthNames[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
  }

  /**
   * Get current month in the format expected by the API
   */
  getCurrentMonth() {
    return this.formatMonth(new Date());
  }

  /**
   * Get last N months for filtering
   */
  getRecentMonths(count = 12) {
    const months = [];
    const now = new Date();
    
    for (let i = 0; i < count; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(this.formatMonth(date));
    }
    
    return months;
  }

  /**
   * Export commission data to CSV (client-side)
   */
  exportToCSV(records, filename = 'commission-tracker') {
    if (!records || records.length === 0) {
      throw new Error('No data to export');
    }

    // Define CSV headers based on your Apps Script structure
    const headers = [
      'Deal Link',
      'Database ID',
      'WellSky Full Name',
      'HubSpot Full Name',
      'HubSpot Client Name',
      'Agency Subdomain',
      'Start Date',
      'Close Date',
      'Inside Sales Rep',
      'Rep Commission',
      'Manager Commission',
      'Total Commission',
      'Status',
      'Commission Month'
    ];

    // Convert records to CSV format
    const csvContent = [
      headers.join(','),
      ...records.map(record => [
        `"${record.dealId || ''}"`,
        `"${record.databaseId || ''}"`,
        `"${record.wellskyFullName || ''}"`,
        `"${record.hubspotFullName || ''}"`,
        `"${record.hubspotClientName || ''}"`,
        `"${record.agencySubdomain || ''}"`,
        record.startDate ? new Date(record.startDate).toLocaleDateString() : '',
        record.closeDate ? new Date(record.closeDate).toLocaleDateString() : '',
        `"${record.isrName || ''}"`,
        record.repCommission || 0,
        record.managerCommission || 0,
        record.totalCommission || 0,
        `"${record.status || ''}"`,
        `"${record.commissionMonth || ''}"`
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  /**
   * Calculate commission totals for display
   */
  calculateTotals(records) {
    if (!records || records.length === 0) {
      return {
        totalRecords: 0,
        totalCommission: 0,
        totalRepCommission: 0,
        totalManagerCommission: 0,
        avgCommissionPerDeal: 0
      };
    }

    const totals = records.reduce((acc, record) => {
      acc.totalRecords++;
      acc.totalCommission += record.totalCommission || 0;
      acc.totalRepCommission += record.repCommission || 0;
      acc.totalManagerCommission += record.managerCommission || 0;
      return acc;
    }, {
      totalRecords: 0,
      totalCommission: 0,
      totalRepCommission: 0,
      totalManagerCommission: 0
    });

    totals.avgCommissionPerDeal = totals.totalRecords > 0 
      ? totals.totalCommission / totals.totalRecords 
      : 0;

    return totals;
  }
}

// Export singleton instance
const commissionTrackerService = new CommissionTrackerService();
export default commissionTrackerService;
